import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTenant } from '../../context/TenantContext';
import * as api from '../../services/routing/api';
import { useTenantConfigQuery } from '../../services/routing/queries';
import { CallbacksPanel } from './CallbacksPanel';
import { NightlySchedulePanel } from './NightlySchedulePanel';
import { RunProfilesPanel } from './RunProfilesPanel';
import type { ParameterCatalogEntry, ParameterRule } from '../../types/routing';

const ALWAYS = '1970-01-01';

const ENGINES = ['osrm', 'valhalla', 'myle', 'manual'];

const URL_FIELDS = [
  { key: 'osrm_url', label: 'osrm_url' },
  { key: 'osrm_ny_url', label: 'osrm_ny_url' },
  { key: 'valhalla_url', label: 'valhalla_url' },
  { key: 'myle_url', label: 'myle_url' }
] as const;

// One engine process serves one road graph, so a market that crosses a state line needs the
// engine for each area it reaches into. A comma-separated list, the way the deployment names
// them — this is an ops screen, not an address book.
const AREA_URLS_LABEL = 'myle_area_urls';

const areaUrlList = (raw: string) =>
  raw
    .split(',')
    .map(url => url.trim())
    .filter(Boolean);

const emptyScopedRule = {
  name: '',
  value: '',
  state: '',
  broker: '',
  vehicle_type: '',
  service_level: '',
  effective_from: ''
};

/** A rule with no dimensions is the market-wide value the parameter table edits. */
const isMarketWide = (rule: ParameterRule) =>
  !rule.state && !rule.broker && !rule.vehicle_type && !rule.service_level;

const scopeLabel = (rule: ParameterRule) =>
  [rule.state, rule.broker, rule.vehicle_type, rule.service_level]
    .filter(Boolean)
    .join(' · ') || 'everywhere';

export const TenantConfigPage = () => {
  const { tenant } = useTenant();
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [scoped, setScoped] = useState(emptyScopedRule);
  const [engine, setEngine] = useState<string | null>(null);
  const [urlEdits, setUrlEdits] = useState<Record<string, string>>({});
  const [areaEdit, setAreaEdit] = useState<string>();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useTenantConfigQuery(tenant);

  // On the tenant, never on the data: a sibling panel saving its own settings refetches this same
  // query, and clearing here on every refetch would throw away parameter and engine edits nobody
  // had saved yet.
  useEffect(() => {
    setEdits({});
    setScoped(emptyScopedRule);
    setEngine(null);
    setUrlEdits({});
  }, [tenant]);

  const catalog: ParameterCatalogEntry[] = data?.catalog ?? [];
  const rules: ParameterRule[] = data?.rules ?? [];
  const marketWide = new Map(
    rules.filter(rule => isMarketWide(rule) && rule.effective_from === ALWAYS)
      .map(rule => [rule.name, rule.value])
  );
  const scopedRules = rules.filter(rule => !isMarketWide(rule) || rule.effective_from !== ALWAYS);

  const valueFor = (name: string) => edits[name] ?? marketWide.get(name) ?? '';

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['tenant-config', tenant] });
    queryClient.invalidateQueries({ queryKey: ['tenants'] });
  };

  const save = async (body: api.TenantConfigUpdate, message: string) => {
    try {
      await api.putTenantConfig(tenant, body);
      enqueueSnackbar(message);
      refresh();
    } catch (saveError) {
      enqueueSnackbar((saveError as Error).message, { variant: 'error' });
    }
  };

  const onSave = async () => {
    const changes = Object.entries(edits)
      .filter(([name, value]) => value !== (marketWide.get(name) ?? ''))
      .map(([name, value]) => ({ name, value }));
    if (!changes.length) {
      enqueueSnackbar('Nothing changed');
      return;
    }
    await save({ parameters: changes }, `Saved ${changes.length} change(s)`);
  };

  const onAddScoped = async () => {
    if (!scoped.name || !scoped.value.trim()) {
      enqueueSnackbar('A scoped rule needs a parameter and a value', { variant: 'error' });
      return;
    }
    await save({ parameters: [scoped] }, `Added a ${scoped.name} rule`);
  };

  const onDeleteScoped = async (rule: ParameterRule) =>
    save({ parameters: [{ ...rule, value: '' }] }, `Deleted the ${rule.name} rule`);

  // Only what the operator touched is sent: an untouched URL keeps whatever the tenant has, where
  // a blank one drops back to the deployment's.
  const onSaveEngine = async () => {
    const body: api.TenantConfigUpdate = {};
    if (engine !== null) body.engine = engine;
    const urls: NonNullable<api.TenantConfigUpdate['engine_urls']> = Object.fromEntries(
      URL_FIELDS.filter(field => urlEdits[field.key] !== undefined).map(field => [
        field.key,
        urlEdits[field.key]
      ])
    );
    if (areaEdit !== undefined) urls.myle_area_urls = areaUrlList(areaEdit);
    if (Object.keys(urls).length) body.engine_urls = urls;
    if (!Object.keys(body).length) {
      enqueueSnackbar('Nothing changed');
      return;
    }
    await save(body, 'Engine saved');
  };

  const onClear = async () => {
    if (
      !window.confirm(
        `Delete ALL parameter rules for tenant "${tenant}"? Runs fall back to the built-in defaults.`
      )
    ) {
      return;
    }
    try {
      const result = await api.clearTenantConfig(tenant);
      enqueueSnackbar(`Deleted ${result.deleted} rule(s)`);
      refresh();
    } catch (clearError) {
      enqueueSnackbar((clearError as Error).message, { variant: 'error' });
    }
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{(error as Error).message}</Alert>}
      {isLoading && tenant && <CircularProgress aria-label="loading tenant config" />}

      {data && (
        <>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              PARAMETERS FOR “{tenant}”
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These seventeen are everything the router can be told. The tenant comes from the
              picker in the top bar; a run resolves the built-in default, then this tenant&apos;s
              rules, then any runtime override (Admin tab). Values here apply market-wide — scope
              one to a broker, vehicle type or service level below. Blank a value to drop the rule.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '40%' }}>Parameter</TableCell>
                    <TableCell sx={{ width: '12%' }}>Unit</TableCell>
                    <TableCell sx={{ width: '14%' }}>Default</TableCell>
                    <TableCell sx={{ width: '14%' }}>Effective</TableCell>
                    <TableCell>This tenant</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catalog.map(parameter => (
                    <TableRow key={parameter.name}>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {parameter.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {parameter.description}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {parameter.unit}
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}
                      >
                        {parameter.default}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {data.effective[parameter.name]}
                      </TableCell>
                      <TableCell>
                        {parameter.options ? (
                          <TextField
                            fullWidth
                            select
                            size="small"
                            SelectProps={{
                              displayEmpty: true,
                              SelectDisplayProps: { 'aria-label': parameter.name }
                            }}
                            value={valueFor(parameter.name)}
                            onChange={event =>
                              setEdits(previous => ({
                                ...previous,
                                [parameter.name]: event.target.value
                              }))
                            }
                          >
                            <MenuItem value="">(use built-in default)</MenuItem>
                            {parameter.options.map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            inputProps={{ 'aria-label': parameter.name }}
                            placeholder={parameter.default}
                            value={valueFor(parameter.name)}
                            onChange={event =>
                              setEdits(previous => ({
                                ...previous,
                                [parameter.name]: event.target.value
                              }))
                            }
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Button variant="contained" onClick={onSave}>
                Save changes
              </Button>
              <Button color="error" onClick={onClear}>
                Delete all rules for this tenant
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              DIRECTIONS ENGINE
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Which engine this tenant&apos;s runs drive, and where it lives. Not a business
              parameter, but stored with the tenant, so it survives a restart. Leave a field blank
              to fall through to the deployment&apos;s
              {data.engine.effective_engine ? ` (currently ${data.engine.effective_engine})` : ''};
              a runtime override on the Admin tab still wins over both while it is set.
            </Typography>
            <Stack spacing={2} sx={{ maxWidth: 520 }}>
              <TextField
                select
                size="small"
                label="engine"
                // A displayEmpty select renders its own text at value '', so the label has to be
                // told to float or the two are drawn on top of each other.
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, SelectDisplayProps: { 'aria-label': 'engine' } }}
                value={engine ?? data.engine.engine}
                onChange={event => setEngine(event.target.value)}
              >
                <MenuItem value="">(use the deployment&apos;s)</MenuItem>
                {ENGINES.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              {URL_FIELDS.map(field => (
                <TextField
                  key={field.key}
                  size="small"
                  label={field.label}
                  InputLabelProps={{ shrink: true }}
                  placeholder={data.engine.effective_engine_urls[field.key] || "(engine default)"}
                  helperText={
                    (urlEdits[field.key] ?? data.engine.engine_urls[field.key]) === ''
                      ? `deployment: ${data.engine.effective_engine_urls[field.key] || 'none'}`
                      : undefined
                  }
                  inputProps={{ 'aria-label': field.label }}
                  value={urlEdits[field.key] ?? data.engine.engine_urls[field.key]}
                  onChange={event =>
                    setUrlEdits(previous => ({ ...previous, [field.key]: event.target.value }))
                  }
                />
              ))}
              <TextField
                size="small"
                label={AREA_URLS_LABEL}
                InputLabelProps={{ shrink: true }}
                placeholder={
                  data.engine.effective_engine_urls.myle_area_urls.join(', ') || '(none — one area)'
                }
                helperText="The engines for the other areas this tenant's work reaches into,
                  comma-separated. Blank means the primary is the only graph, and every stop off
                  it is unroutable."
                inputProps={{ 'aria-label': AREA_URLS_LABEL }}
                value={areaEdit ?? data.engine.engine_urls.myle_area_urls.join(', ')}
                onChange={event => setAreaEdit(event.target.value)}
              />
              <Stack direction="row">
                <Button variant="contained" onClick={onSaveEngine}>
                  Save engine
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              SCOPED RULES
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A rule can be narrowed to a state, a broker, a vehicle type or a service level, and
              dated from when it takes effect. The rule matching the most dimensions wins; ties
              break state, then broker, then vehicle type, then service level.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Parameter</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Broker</TableCell>
                    <TableCell>Vehicle type</TableCell>
                    <TableCell>Service level</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scopedRules.map(rule => (
                    <TableRow
                      key={`${rule.name}|${scopeLabel(rule)}|${rule.effective_from}`}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {rule.name}
                      </TableCell>
                      <TableCell>{rule.value}</TableCell>
                      <TableCell>{rule.state}</TableCell>
                      <TableCell>{rule.broker}</TableCell>
                      <TableCell>{rule.vehicle_type}</TableCell>
                      <TableCell>{rule.service_level}</TableCell>
                      <TableCell>{rule.effective_from}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={`delete ${rule.name} for ${scopeLabel(rule)}`}
                          onClick={() => onDeleteScoped(rule)}
                        >
                          ✕
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        SelectProps={{
                          displayEmpty: true,
                          SelectDisplayProps: { 'aria-label': 'scoped parameter' }
                        }}
                        value={scoped.name}
                        onChange={event => setScoped({ ...scoped, name: event.target.value })}
                      >
                        <MenuItem value="">(choose)</MenuItem>
                        {catalog.map(parameter => (
                          <MenuItem key={parameter.name} value={parameter.name}>
                            {parameter.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        inputProps={{ 'aria-label': 'scoped value' }}
                        value={scoped.value}
                        onChange={event => setScoped({ ...scoped, value: event.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        inputProps={{ 'aria-label': 'scoped state' }}
                        value={scoped.state}
                        onChange={event => setScoped({ ...scoped, state: event.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        inputProps={{ 'aria-label': 'scoped broker' }}
                        value={scoped.broker}
                        onChange={event => setScoped({ ...scoped, broker: event.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        inputProps={{ 'aria-label': 'scoped vehicle type' }}
                        value={scoped.vehicle_type}
                        onChange={event =>
                          setScoped({ ...scoped, vehicle_type: event.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        inputProps={{ 'aria-label': 'scoped service level' }}
                        value={scoped.service_level}
                        onChange={event =>
                          setScoped({ ...scoped, service_level: event.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        placeholder="yyyy-mm-dd"
                        inputProps={{ 'aria-label': 'scoped effective from' }}
                        value={scoped.effective_from}
                        onChange={event =>
                          setScoped({ ...scoped, effective_from: event.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={onAddScoped}>
                        Add
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <CallbacksPanel tenant={tenant} />

          <RunProfilesPanel tenant={tenant} />

          <NightlySchedulePanel tenant={tenant} />
        </>
      )}
    </Stack>
  );
};

export default TenantConfigPage;
